import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Truck, Package, Save, ChevronDown, ChevronRight, FileText, CheckCircle, Upload, Paperclip, X, Download, AlertTriangle, BarChart3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const CustomerOutwardDocs = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterTransport, setFilterTransport] = useState('');
  const [transports, setTransports] = useState([]);
  const [expandedDCs, setExpandedDCs] = useState(new Set());
  const [editingLR, setEditingLR] = useState({});
  const [saving, setSaving] = useState({});
  const [uploading, setUploading] = useState({});
  const [pendingLrDays, setPendingLrDays] = useState('');  // '' = no filter
  const [pendingLrInput, setPendingLrInput] = useState('');
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const importInputRef = useRef(null);

  // Reports modal state
  const [reportOpen, setReportOpen] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportData, setReportData] = useState({ transport: null, state: null, customer: null });
  const [reportTab, setReportTab] = useState('transport');
  const [reportDateFrom, setReportDateFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [reportDateTo, setReportDateTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [reportExporting, setReportExporting] = useState(false);
  // 2026-02 — Category-4 filter on the Reports modal. Pulls distinct
  // values from `item_master.category4` and applies to all 3 tabs
  // (Transporter / State / Customer). Empty selection ⇒ all categories.
  const [reportCategories, setReportCategories] = useState([]);            // available options
  const [reportSelectedCategories, setReportSelectedCategories] = useState([]); // selected
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  // 2026-02 — All vs Tagged toggle. "Tagged" restricts the aggregation to
  // DCs whose LR doc is fully populated (lr_number, lr_date, delivery_date,
  // freight_charge > 0), so TAT + cost averages reflect only the
  // updated docs and not the in-progress backlog.
  const [reportTaggedOnly, setReportTaggedOnly] = useState(false);

  const LIMIT = 50;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(LIMIT) });
      if (search.trim()) params.append('search', search.trim());
      if (filterTransport) params.append('transport', filterTransport);
      if (pendingLrDays && Number(pendingLrDays) > 0) params.append('pending_lr_days', String(pendingLrDays));
      const res = await apiClient.get(`/customer-outward-docs/entries?${params}`);
      setEntries(res.data.entries || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error('Failed to load entries');
    } finally {
      setLoading(false);
    }
  }, [page, filterTransport, pendingLrDays]);

  useEffect(() => {
    apiClient.get('/customer-outward-docs/transports').then(r => setTransports(r.data.transports || [])).catch(() => {});
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleExpand = (dc) => {
    setExpandedDCs(prev => {
      const next = new Set(prev);
      next.has(dc) ? next.delete(dc) : next.add(dc);
      return next;
    });
  };

  // ---- Pending LR > X days ----
  const applyPendingFilter = () => {
    const v = (pendingLrInput || '').trim();
    if (!v) {
      setPendingLrDays('');
      setPage(0);
      return;
    }
    const n = Number(v);
    if (!Number.isFinite(n) || n < 1 || n > 365) {
      toast.error('Enter a number between 1 and 365');
      return;
    }
    setPendingLrDays(String(Math.floor(n)));
    setPage(0);
  };
  const clearPendingFilter = () => {
    setPendingLrInput('');
    setPendingLrDays('');
    setPage(0);
  };

  // ---- Import CSV ----
  const handleImportClick = () => importInputRef.current?.click();
  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please select a .csv file');
      return;
    }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await apiClient.post('/customer-outward-docs/import-lr-csv', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const d = res.data || {};
      toast.success(d.message || `Imported ${d.updated || 0} row(s)`, { duration: 7000 });
      if (d.errors?.length) {
        console.warn('Import errors:', d.errors);
        toast.warning(`${d.errors.length} row(s) skipped — see console for details`, { duration: 9000 });
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  // ---- Export CSV ----
  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.append('search', search.trim());
      if (filterTransport) params.append('transport', filterTransport);
      if (pendingLrDays) params.append('pending_lr_days', String(pendingLrDays));
      const res = await apiClient.get(`/customer-outward-docs/export-csv?${params}`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-');
      a.download = `customer_outward_documentation_${stamp}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  // ---- Reports ----
  const fetchReportTab = async (tabKey, overrideCategories = null, overrideTagged = null) => {
    setReportLoading(true);
    try {
      const params = new URLSearchParams({ group_by: tabKey });
      if (reportDateFrom) params.append('date_from', reportDateFrom);
      if (reportDateTo) params.append('date_to', reportDateTo);
      const cats = overrideCategories ?? reportSelectedCategories;
      if (cats && cats.length > 0) params.append('categories', cats.join(','));
      const tagged = overrideTagged ?? reportTaggedOnly;
      if (tagged) params.append('tagged_only', 'true');
      const res = await apiClient.get(`/customer-outward-docs/report?${params}`);
      setReportData(prev => ({ ...prev, [tabKey]: res.data }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load report');
    } finally {
      setReportLoading(false);
    }
  };

  const fetchCategoryOptions = async () => {
    try {
      const res = await apiClient.get('/customer-outward-docs/report/categories');
      setReportCategories(res.data?.categories || []);
    } catch (err) {
      // Silently fall back to no options — the filter remains a no-op.
      setReportCategories([]);
    }
  };

  const openReports = () => {
    setReportData({ transport: null, state: null, customer: null });
    setReportTab('transport');
    setReportOpen(true);
    // Refresh category options on every open (cheap distinct call) so
    // newly-added category4 values surface without a page reload.
    fetchCategoryOptions();
    // Fetch the first tab on open
    setTimeout(() => fetchReportTab('transport'), 0);
  };

  const onReportTabChange = (v) => {
    setReportTab(v);
    if (!reportData[v]) fetchReportTab(v);
  };

  const refreshReport = () => {
    setReportData({ transport: null, state: null, customer: null });
    fetchReportTab(reportTab);
  };

  // 2026-02 — Toggle a single category on the dropdown. Tab data is
  // invalidated so the next tab open / Apply click refetches with
  // the new filter. We do NOT auto-refetch on every toggle to keep
  // the UI responsive while the operator picks multiple categories.
  const toggleCategory = (cat) => {
    setReportSelectedCategories(prev =>
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    );
    setReportData({ transport: null, state: null, customer: null });
  };

  const toggleAllCategories = () => {
    const allSelected =
      reportCategories.length > 0
      && reportSelectedCategories.length === reportCategories.length;
    setReportSelectedCategories(allSelected ? [] : [...reportCategories]);
    setReportData({ transport: null, state: null, customer: null });
  };

  // Flip All ↔ Tagged. Invalidates cached tab payloads so the next
  // Apply / tab switch refetches against the new aggregation scope.
  const toggleTaggedOnly = () => {
    setReportTaggedOnly(prev => !prev);
    setReportData({ transport: null, state: null, customer: null });
  };

  const handleReportExport = async () => {
    setReportExporting(true);
    try {
      const params = new URLSearchParams({ group_by: reportTab });
      if (reportDateFrom) params.append('date_from', reportDateFrom);
      if (reportDateTo) params.append('date_to', reportDateTo);
      if (reportSelectedCategories.length > 0) {
        params.append('categories', reportSelectedCategories.join(','));
      }
      if (reportTaggedOnly) params.append('tagged_only', 'true');
      const res = await apiClient.get(`/customer-outward-docs/report/export-csv?${params}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-');
      a.download = `outward_report_${reportTab}_${stamp}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('Report CSV downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export failed');
    } finally {
      setReportExporting(false);
    }
  };

  const handleLRChange = (dc, field, value) => {
    setEditingLR(prev => ({ ...prev, [dc]: { ...prev[dc], [field]: value } }));
  };

  const getLRValue = (entry, field) => {
    if (editingLR[entry.dc_number]?.[field] !== undefined) return editingLR[entry.dc_number][field];
    return entry[field] || '';
  };

  const handleSaveLR = async (entry) => {
    const edits = editingLR[entry.dc_number] || {};
    setSaving(prev => ({ ...prev, [entry.dc_number]: true }));
    try {
      await apiClient.post('/customer-outward-docs/update-lr', {
        dc_number: entry.dc_number,
        lr_number: edits.lr_number ?? entry.lr_number ?? '',
        lr_date: edits.lr_date ?? entry.lr_date ?? '',
        delivery_date: edits.delivery_date ?? entry.delivery_date ?? '',
        freight_charge: parseFloat(edits.freight_charge ?? entry.freight_charge ?? 0) || 0,
      });
      toast.success('LR details saved');
      setEntries(prev => prev.map(e => e.dc_number === entry.dc_number ? {
        ...e,
        lr_number: edits.lr_number ?? e.lr_number,
        lr_date: edits.lr_date ?? e.lr_date,
        delivery_date: edits.delivery_date ?? e.delivery_date,
        freight_charge: parseFloat(edits.freight_charge ?? e.freight_charge ?? 0) || 0,
        lr_updated: true,
      } : e));
      setEditingLR(prev => { const n = { ...prev }; delete n[entry.dc_number]; return n; });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(prev => ({ ...prev, [entry.dc_number]: false }));
    }
  };

  const handlePodUpload = async (dc, files) => {
    if (!files?.length) return;
    setUploading(prev => ({ ...prev, [dc]: true }));
    try {
      const urls = [];
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('folder', `pod/${dc}`);
        const resp = await apiClient.post('/storage/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        urls.push(resp.data.url);
      }
      await apiClient.post('/customer-outward-docs/upload-pod', { dc_number: dc, pod_urls: [...(entries.find(e => e.dc_number === dc)?.pod_urls || []), ...urls] });
      toast.success(`POD uploaded`);
      setEntries(prev => prev.map(e => e.dc_number === dc ? { ...e, pod_urls: [...(e.pod_urls || []), ...urls] } : e));
    } catch { toast.error('Failed to upload POD'); }
    finally { setUploading(prev => ({ ...prev, [dc]: false })); }
  };

  const handleRemovePod = async (dc, urlToRemove) => {
    const updated = (entries.find(e => e.dc_number === dc)?.pod_urls || []).filter(u => u !== urlToRemove);
    try {
      await apiClient.post('/customer-outward-docs/upload-pod', { dc_number: dc, pod_urls: updated });
      setEntries(prev => prev.map(e => e.dc_number === dc ? { ...e, pod_urls: updated } : e));
    } catch { toast.error('Failed to remove'); }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    try {
      // Convert UTC to IST (UTC+5:30) and display
      const utc = new Date(d);
      const ist = new Date(utc.getTime() + (5.5 * 60 * 60 * 1000));
      const dd = String(ist.getUTCDate()).padStart(2, '0');
      const mm = String(ist.getUTCMonth() + 1).padStart(2, '0');
      const yyyy = ist.getUTCFullYear();
      return `${dd}/${mm}/${yyyy}`;
    } catch { return d; }
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="customer-outward-docs-page">
      <div className="bg-[#1A1F2E] text-white px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="text-white hover:bg-white/10" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-lg font-bold">Customer Outward Documentation</h1>
          <p className="text-xs text-gray-300">LR Details, Delivery Dates & Freight Charges — by DC No</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 space-y-4">
        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-start">
          <div className="flex gap-2 flex-1 min-w-[200px]">
            <Input value={search} onChange={e => setSearch(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { setPage(0); fetchData(); } }}
              placeholder="Search by DC no, pick slip no, customer, gate pass..." data-testid="search-input" />
            <Button onClick={() => { setPage(0); fetchData(); }} data-testid="search-btn"><Search className="w-4 h-4" /></Button>
          </div>
          <Select value={filterTransport} onValueChange={v => { setFilterTransport(v === 'all' ? '' : v); setPage(0); }}>
            <SelectTrigger className="w-[220px]" data-testid="transport-filter">
              <SelectValue placeholder="All Transports" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Transports</SelectItem>
              {transports.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>

          {/* Pending LR > X days filter */}
          <div className="flex items-center gap-1 border rounded-md px-2 h-10 bg-white" data-testid="pending-lr-filter">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
            <span className="text-xs text-gray-600 whitespace-nowrap">Pending LR &gt;</span>
            <Input
              type="number"
              min="1"
              max="365"
              value={pendingLrInput}
              onChange={e => setPendingLrInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') applyPendingFilter(); }}
              placeholder="X"
              className="w-14 h-7 text-xs px-1 border-0 focus-visible:ring-0"
              data-testid="pending-lr-days-input"
            />
            <span className="text-xs text-gray-600">days</span>
            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs"
              onClick={applyPendingFilter} data-testid="pending-lr-apply-btn">
              Apply
            </Button>
            {pendingLrDays && (
              <Button size="sm" variant="ghost" className="h-7 px-1 text-xs text-gray-500 hover:text-red-600"
                onClick={clearPendingFilter} data-testid="pending-lr-clear-btn">
                <X className="w-3 h-3" />
              </Button>
            )}
          </div>

          {/* Import / Export */}
          <input
            ref={importInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={handleImportFile}
            data-testid="import-csv-file-input"
          />
          <Button
            variant="outline"
            onClick={handleImportClick}
            disabled={importing}
            className="h-10"
            data-testid="import-csv-btn"
          >
            <Upload className="w-4 h-4 mr-1" />
            {importing ? 'Importing…' : 'Import CSV'}
          </Button>
          <Button
            variant="outline"
            onClick={handleExportCsv}
            disabled={exporting}
            className="h-10"
            data-testid="export-csv-btn"
          >
            <Download className="w-4 h-4 mr-1" />
            {exporting ? 'Exporting…' : 'Export CSV'}
          </Button>
          <Button
            variant="outline"
            onClick={openReports}
            className="h-10 border-indigo-300 text-indigo-700 hover:bg-indigo-50"
            data-testid="open-reports-btn"
          >
            <BarChart3 className="w-4 h-4 mr-1" />
            Reports
          </Button>

          <Badge variant="outline" className="h-10 px-3 flex items-center" data-testid="dc-count-badge">{total} DCs</Badge>
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-400">Loading...</div>
        ) : entries.length === 0 ? (
          <div className="text-center py-12 text-gray-400">No entries found</div>
        ) : (
          <Card>
            <CardContent className="p-0 divide-y">
              {entries.map(entry => {
                const isExpanded = expandedDCs.has(entry.dc_number);
                const isSaving = saving[entry.dc_number];

                return (
                  <div key={entry.dc_number} data-testid={`dc-${entry.dc_number}`}>
                    {/* DC row */}
                    <div className="px-4 py-3 flex items-center gap-4 cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => toggleExpand(entry.dc_number)}>
                      <button className="shrink-0">
                        {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                      </button>
                      <div className="flex-1 grid grid-cols-7 gap-3 text-sm items-center">
                        <div>
                          <p className="font-mono font-bold" data-testid={`dc-label-${entry.dc_number}`}>
                            {entry.original_dc_number || entry.dc_number}
                          </p>
                          {entry.original_dc_number && (
                            <p className="text-[10px] text-gray-400 font-mono">PS #{entry.dc_number}</p>
                          )}
                          <p className="text-xs text-gray-400">{formatDate(entry.created_at)}</p>
                        </div>
                        <div>
                          <p className="font-medium">{entry.customer}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 flex items-center gap-1"><Truck className="w-3 h-3" />{entry.transport_name || '-'}</p>
                        </div>
                        <div className="text-center">
                          <p className="font-bold">{entry.total_boxes}</p>
                          <p className="text-xs text-gray-400">boxes</p>
                        </div>
                        <div className="text-center">
                          <p className="font-bold">{entry.total_qty}</p>
                          <p className="text-xs text-gray-400">pcs</p>
                        </div>
                        <div>
                          {entry.lr_updated ? (
                            <Badge className="bg-green-100 text-green-800 border-green-200">
                              <CheckCircle className="w-3 h-3 mr-1" /> LR: {entry.lr_number}
                            </Badge>
                          ) : (
                            <Badge className="bg-amber-50 text-amber-700 border-amber-200">Pending LR</Badge>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-xs font-mono text-gray-400">{entry.gate_pass_number}</p>
                          <p className="text-xs text-gray-400">{entry.vehicle_number || ''}</p>
                        </div>
                      </div>
                    </div>

                    {/* Expanded: LR form + boxes */}
                    {isExpanded && (
                      <div className="px-6 pb-4 bg-gray-50/50 space-y-4">
                        {/* LR Details */}
                        <div className="bg-white rounded-lg border p-4" data-testid={`lr-form-${entry.dc_number}`}>
                          <h4 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                            <FileText className="w-4 h-4" /> L.R. Details — {entry.original_dc_number || entry.dc_number}
                            {entry.original_dc_number && (
                              <span className="text-xs font-mono text-gray-400 font-normal">(PS #{entry.dc_number})</span>
                            )}
                          </h4>
                          <div className="grid grid-cols-4 gap-3">
                            <div>
                              <label className="text-xs font-medium text-gray-500 mb-1 block">L.R. Number</label>
                              <Input value={getLRValue(entry, 'lr_number')} onChange={e => handleLRChange(entry.dc_number, 'lr_number', e.target.value)} placeholder="LR number..." />
                            </div>
                            <div>
                              <label className="text-xs font-medium text-gray-500 mb-1 block">L.R. Date</label>
                              <Input type="date" value={getLRValue(entry, 'lr_date')} onChange={e => handleLRChange(entry.dc_number, 'lr_date', e.target.value)} />
                            </div>
                            <div>
                              <label className="text-xs font-medium text-gray-500 mb-1 block">Delivery Date</label>
                              <Input type="date" value={getLRValue(entry, 'delivery_date')} onChange={e => handleLRChange(entry.dc_number, 'delivery_date', e.target.value)} />
                            </div>
                            <div>
                              <label className="text-xs font-medium text-gray-500 mb-1 block">Freight Charge</label>
                              <div className="flex gap-2">
                                <Input type="number" min="0" step="0.01" value={getLRValue(entry, 'freight_charge')} onChange={e => handleLRChange(entry.dc_number, 'freight_charge', e.target.value)} placeholder="0.00" />
                                <Button size="sm" onClick={() => handleSaveLR(entry)} disabled={isSaving} data-testid={`save-lr-${entry.dc_number}`}>
                                  {isSaving ? '...' : <Save className="w-4 h-4" />}
                                </Button>
                              </div>
                            </div>
                          </div>
                          {/* POD Upload */}
                          <div className="mt-3 pt-3 border-t">
                            <label className="text-xs font-medium text-gray-500 mb-2 block flex items-center gap-1">
                              <Paperclip className="w-3.5 h-3.5" /> POD Copy
                            </label>
                            <div className="flex items-center gap-3">
                              <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm transition-colors">
                                <Upload className="w-3.5 h-3.5" />
                                {uploading[entry.dc_number] ? 'Uploading...' : 'Upload POD'}
                                <input type="file" accept="image/*,.pdf" multiple className="hidden"
                                  onChange={e => handlePodUpload(entry.dc_number, e.target.files)} disabled={uploading[entry.dc_number]} />
                              </label>
                              {(entry.pod_urls || []).map((url, idx) => (
                                <div key={idx} className="flex items-center gap-1 bg-green-50 border border-green-200 rounded px-2 py-1 text-xs">
                                  <a href={url} target="_blank" rel="noopener noreferrer" className="text-green-700 hover:underline font-medium">POD {idx + 1}</a>
                                  <button onClick={() => handleRemovePod(entry.dc_number, url)} className="text-red-400 hover:text-red-600 ml-1"><X className="w-3 h-3" /></button>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* Boxes */}
                        {entry.boxes?.length > 0 && (
                          <div>
                            <h4 className="text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                              <Package className="w-4 h-4" /> Boxes ({entry.total_boxes})
                            </h4>
                            <div className="space-y-2">
                              {entry.boxes.map(box => (
                                <div key={box.box_number} className="bg-white rounded-lg border px-3 py-2">
                                  <div className="flex items-center justify-between mb-1.5">
                                    <span className="text-sm font-mono font-bold">{box.box_number}</span>
                                    <div className="flex items-center gap-3 text-xs text-gray-500">
                                      <span>{box.total_qty} pcs</span>
                                      {box.weight_kg > 0 && <span>{box.weight_kg} kg</span>}
                                    </div>
                                  </div>
                                  {box.items?.length > 0 && (
                                    <Table>
                                      <TableHeader>
                                        <TableRow>
                                          <TableHead className="text-xs py-1">Design</TableHead>
                                          <TableHead className="text-xs py-1">Barcode</TableHead>
                                          <TableHead className="text-xs py-1">Shade</TableHead>
                                          <TableHead className="text-xs py-1 text-center">Qty</TableHead>
                                          <TableHead className="text-xs py-1">Order</TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {box.items.map((item, idx) => (
                                          <TableRow key={idx}>
                                            <TableCell className="text-xs py-1">{item.design_name || '-'}</TableCell>
                                            <TableCell className="text-xs py-1 font-mono">{item.barcode || '-'}</TableCell>
                                            <TableCell className="text-xs py-1">{item.shade || '-'}</TableCell>
                                            <TableCell className="text-xs py-1 text-center font-bold">{item.qty}</TableCell>
                                            <TableCell className="text-xs py-1">{item.order_no || '-'}</TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-between items-center pt-2">
            <span className="text-sm text-gray-500">Page {page + 1} of {totalPages}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>Prev</Button>
              <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={(page + 1) >= totalPages}>Next</Button>
            </div>
          </div>
        )}
      </div>

      {/* Reports Modal */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="max-w-6xl" data-testid="reports-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-indigo-600" />
              Customer Outward Reports
            </DialogTitle>
            <DialogDescription>
              TAT (LR → Delivery), freight cost breakdown and weight averages — grouped by Transport, State or Customer.
            </DialogDescription>
          </DialogHeader>

          {/* Date filters */}
          <div className="flex flex-wrap items-end gap-3 pb-2 border-b">
            <div>
              <label className="text-xs text-gray-500 block mb-1">From</label>
              <Input
                type="date"
                value={reportDateFrom}
                onChange={(e) => setReportDateFrom(e.target.value)}
                className="h-9 w-[160px]"
                data-testid="report-date-from"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">To</label>
              <Input
                type="date"
                value={reportDateTo}
                onChange={(e) => setReportDateTo(e.target.value)}
                className="h-9 w-[160px]"
                data-testid="report-date-to"
              />
            </div>
            {/* 2026-02 — Category-4 multi-select filter. Applies to all
                3 tabs (Transporter / State / Customer). */}
            <div className="relative">
              <label className="text-xs text-gray-500 block mb-1">Category</label>
              <button
                type="button"
                onClick={() => setCategoryDropdownOpen(o => !o)}
                className="h-9 min-w-[200px] max-w-[280px] border rounded-md px-3 text-sm flex items-center justify-between bg-white hover:bg-gray-50"
                data-testid="report-category-trigger"
              >
                <span className="truncate text-left flex-1">
                  {reportSelectedCategories.length === 0
                    ? 'All categories'
                    : reportSelectedCategories.length === reportCategories.length
                      ? 'All categories'
                      : reportSelectedCategories.length === 1
                        ? reportSelectedCategories[0]
                        : `${reportSelectedCategories.length} selected`}
                </span>
                <ChevronDown
                  className={`w-4 h-4 ml-1 text-gray-500 transition-transform ${categoryDropdownOpen ? 'rotate-180' : ''}`}
                />
              </button>
              {categoryDropdownOpen && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setCategoryDropdownOpen(false)}
                  />
                  <div
                    className="absolute z-50 mt-1 w-[280px] bg-white border rounded-md shadow-lg max-h-72 overflow-y-auto"
                    data-testid="report-category-menu"
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <button
                      type="button"
                      onClick={toggleAllCategories}
                      className="w-full text-left px-3 py-2 text-sm font-medium hover:bg-indigo-50 border-b flex items-center gap-2"
                      data-testid="report-category-select-all"
                    >
                      <input
                        type="checkbox"
                        readOnly
                        checked={
                          reportCategories.length > 0 &&
                          reportSelectedCategories.length === reportCategories.length
                        }
                        className="rounded"
                      />
                      <span>Select All</span>
                      <span className="ml-auto text-xs text-gray-400">
                        {reportCategories.length}
                      </span>
                    </button>
                    {reportCategories.length === 0 && (
                      <div className="px-3 py-2 text-xs text-gray-400">
                        No categories found in item_master.
                      </div>
                    )}
                    {reportCategories.map((cat) => {
                      const checked = reportSelectedCategories.includes(cat);
                      return (
                        <label
                          key={cat}
                          className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-indigo-50 cursor-pointer"
                          data-testid={`report-category-option-${cat}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleCategory(cat)}
                            className="rounded"
                          />
                          <span>{cat}</span>
                        </label>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
            <Button onClick={refreshReport} disabled={reportLoading} className="h-9" data-testid="report-apply-btn">
              <Search className="w-4 h-4 mr-1" /> Apply
            </Button>
            {/* 2026-02 — All ↔ Tagged toggle. Tagged restricts every
                computed value to DCs whose LR doc is fully populated. */}
            <div className="flex flex-col">
              <label className="text-xs text-gray-500 block mb-1">Scope</label>
              <div
                className="inline-flex items-center bg-gray-100 rounded-md p-0.5 h-9"
                data-testid="report-tagged-toggle"
              >
                <button
                  type="button"
                  onClick={() => { if (reportTaggedOnly) toggleTaggedOnly(); }}
                  className={
                    'px-3 h-8 text-xs font-medium rounded transition-colors ' +
                    (!reportTaggedOnly
                      ? 'bg-white text-indigo-700 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700')
                  }
                  data-testid="report-scope-all"
                >
                  All
                </button>
                <button
                  type="button"
                  onClick={() => { if (!reportTaggedOnly) toggleTaggedOnly(); }}
                  className={
                    'px-3 h-8 text-xs font-medium rounded transition-colors flex items-center gap-1 ' +
                    (reportTaggedOnly
                      ? 'bg-emerald-600 text-white shadow-sm'
                      : 'text-gray-500 hover:text-gray-700')
                  }
                  data-testid="report-scope-tagged"
                  title="Only count DCs with LR number, LR date, delivery date and freight charge populated"
                >
                  <CheckCircle className="w-3.5 h-3.5" />
                  Tagged
                </button>
              </div>
            </div>
            <div className="flex-1" />
            <Button
              variant="outline"
              onClick={handleReportExport}
              disabled={reportExporting}
              className="h-9 border-indigo-300 text-indigo-700 hover:bg-indigo-50"
              data-testid="report-export-csv-btn"
            >
              <Download className="w-4 h-4 mr-1" />
              {reportExporting ? 'Exporting…' : 'Export Detail CSV'}
            </Button>
          </div>

          <Tabs value={reportTab} onValueChange={onReportTabChange} className="w-full">
            <TabsList className="grid w-full grid-cols-3" data-testid="report-tabs">
              <TabsTrigger value="transport" data-testid="report-tab-transport">By Transport</TabsTrigger>
              <TabsTrigger value="state" data-testid="report-tab-state">By State</TabsTrigger>
              <TabsTrigger value="customer" data-testid="report-tab-customer">By Customer</TabsTrigger>
            </TabsList>

            {['transport', 'state', 'customer'].map(tabKey => (
              <TabsContent key={tabKey} value={tabKey} className="mt-3">
                {reportLoading && reportTab === tabKey ? (
                  <div className="py-10 text-center text-sm text-gray-500">Loading report…</div>
                ) : !reportData[tabKey] ? (
                  <div className="py-10 text-center text-sm text-gray-400">No data loaded yet.</div>
                ) : reportData[tabKey].groups.length === 0 ? (
                  <div className="py-10 text-center text-sm text-gray-400">No DCs in this date range.</div>
                ) : (
                  <div className="max-h-[55vh] overflow-auto rounded border">
                    <Table className="text-xs">
                      <TableHeader className="bg-gray-50 sticky top-0">
                        <TableRow>
                          <TableHead className="capitalize">{tabKey}</TableHead>
                          <TableHead className="text-right">DCs</TableHead>
                          <TableHead className="text-right">Boxes</TableHead>
                          <TableHead className="text-right">Pieces</TableHead>
                          <TableHead className="text-right">Total Kg</TableHead>
                          <TableHead className="text-right">Avg Box Kg</TableHead>
                          <TableHead className="text-right">Avg TAT (d)</TableHead>
                          <TableHead className="text-right">Min/Max TAT</TableHead>
                          <TableHead className="text-right">Total Freight</TableHead>
                          <TableHead className="text-right">Avg /DC</TableHead>
                          <TableHead className="text-right">Avg /Box</TableHead>
                          <TableHead className="text-right">Avg /Bundle</TableHead>
                          <TableHead className="text-right">Avg /Piece</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {reportData[tabKey].groups.map((g, idx) => (
                          <TableRow key={`${tabKey}-${g.group}-${idx}`} data-testid={`report-row-${tabKey}-${idx}`}>
                            <TableCell className="font-medium">{g.group}</TableCell>
                            <TableCell className="text-right">{g.dc_count}</TableCell>
                            <TableCell className="text-right">{g.total_boxes}</TableCell>
                            <TableCell className="text-right">{g.total_qty}</TableCell>
                            <TableCell className="text-right">{g.total_weight_kg}</TableCell>
                            <TableCell className="text-right">{g.avg_box_weight_kg}</TableCell>
                            <TableCell className="text-right">{g.avg_tat_days ?? '-'}</TableCell>
                            <TableCell className="text-right text-gray-500">
                              {g.min_tat_days ?? '-'} / {g.max_tat_days ?? '-'}
                            </TableCell>
                            <TableCell className="text-right font-medium">₹{g.total_freight}</TableCell>
                            <TableCell className="text-right">₹{g.avg_cost_per_dc}</TableCell>
                            <TableCell className="text-right">₹{g.avg_cost_per_box}</TableCell>
                            <TableCell className="text-right">₹{g.avg_cost_per_bundle}</TableCell>
                            <TableCell className="text-right">₹{g.avg_cost_per_piece}</TableCell>
                          </TableRow>
                        ))}
                        {reportData[tabKey].overall && (
                          <TableRow className="bg-indigo-50 font-semibold" data-testid={`report-overall-${tabKey}`}>
                            <TableCell>OVERALL</TableCell>
                            <TableCell className="text-right">{reportData[tabKey].overall.dc_count}</TableCell>
                            <TableCell className="text-right">{reportData[tabKey].overall.total_boxes}</TableCell>
                            <TableCell className="text-right">{reportData[tabKey].overall.total_qty}</TableCell>
                            <TableCell className="text-right">{reportData[tabKey].overall.total_weight_kg}</TableCell>
                            <TableCell className="text-right">{reportData[tabKey].overall.avg_box_weight_kg}</TableCell>
                            <TableCell className="text-right">{reportData[tabKey].overall.avg_tat_days ?? '-'}</TableCell>
                            <TableCell className="text-right text-gray-500">
                              {reportData[tabKey].overall.min_tat_days ?? '-'} / {reportData[tabKey].overall.max_tat_days ?? '-'}
                            </TableCell>
                            <TableCell className="text-right">₹{reportData[tabKey].overall.total_freight}</TableCell>
                            <TableCell className="text-right">₹{reportData[tabKey].overall.avg_cost_per_dc}</TableCell>
                            <TableCell className="text-right">₹{reportData[tabKey].overall.avg_cost_per_box}</TableCell>
                            <TableCell className="text-right">₹{reportData[tabKey].overall.avg_cost_per_bundle}</TableCell>
                            <TableCell className="text-right">₹{reportData[tabKey].overall.avg_cost_per_piece}</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </TabsContent>
            ))}
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => setReportOpen(false)} data-testid="report-close-btn">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerOutwardDocs;
